import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Bot, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { ChatThread } from '@/lib/chat/client';
import { useChatStream } from '@/hooks/useChatStream';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';

interface Props {
  thread: ChatThread;
  agentName: string;
  agentRole?: string | null;
}

// How long the pane can sit in `loading` with no new activity before we tell the
// user the stream likely died (a dropped SSE never dispatches turn_completed, so
// stream.loading stays true forever otherwise — CA-038).
const STALL_MS = 45_000;

export function ChatPane({ thread, agentName, agentRole }: Props) {
  const stream = useChatStream(thread.id);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Text the user tried to send that never made it to the server. Kept so we can
  // show it + a "Reintentar" affordance instead of silently swallowing it — the
  // ChatInput clears its own textarea the moment onSend fires (CA-038a).
  const [failedText, setFailedText] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // True when the stream has been in `loading` with no activity long enough that
  // it's almost certainly a dropped connection (CA-038b).
  const [stalled, setStalled] = useState(false);

  // Autoscroll to bottom on new content while in flight.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [stream.messages, stream.loading]);

  // Reset transient UI state when the thread changes.
  useEffect(() => {
    setFailedText(null);
    setSending(false);
    setStalled(false);
  }, [thread.id]);

  // Stall watchdog: if we're loading, arm a timer that trips unless message
  // activity (which changes stream.messages) resets it first.
  useEffect(() => {
    if (!stream.loading) {
      setStalled(false);
      return;
    }
    setStalled(false);
    const timer = setTimeout(() => setStalled(true), STALL_MS);
    return () => clearTimeout(timer);
  }, [stream.loading, stream.messages]);

  // Surface backend `error` frames (reducer sets stream.error) as a toast once.
  useEffect(() => {
    if (stream.error) toast.error(`El agente reportó un error: ${stream.error}`);
  }, [stream.error]);

  const doSend = async (text: string) => {
    setFailedText(null);
    setSending(true);
    try {
      await stream.send(text);
    } catch (err) {
      // postMessage/openStream rejected — the user's text never reached the
      // agent. Keep it and tell them, instead of losing it silently.
      console.error('[ChatPane] send failed', err);
      setFailedText(text);
      const msg = err instanceof Error ? err.message : 'No se pudo enviar el mensaje';
      toast.error(
        msg === 'not_authenticated'
          ? 'Tu sesión expiró. Recarga la página e inicia sesión de nuevo.'
          : `No se pudo enviar el mensaje: ${msg}`,
      );
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    // Best-effort: abort the in-flight stream so a fresh send can proceed.
    void stream.cancel();
    setStalled(false);
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b border-border/50 bg-background/40 px-5 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
          <Bot className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="truncate font-medium">{agentName}</div>
          <div className="truncate text-xs text-muted-foreground">{agentRole ?? 'Agente'} · {thread.title ?? 'Sin título'}</div>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4">
        <div className="mx-auto max-w-3xl">
          {stream.messages.length === 0 ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
              <Bot className="h-8 w-8 opacity-50" />
              <div>Saluda a {agentName} para empezar.</div>
            </div>
          ) : (
            stream.messages.map((m) => <ChatMessage key={m.id} message={m} />)
          )}
        </div>
      </div>

      {/* CA-038b: stream looks dead — give the user a way out of the stuck spinner */}
      {stalled && (
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 px-4 pb-2 text-xs">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">La respuesta parece haberse interrumpido. Puedes reiniciar y volver a intentar.</span>
            <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px]" onClick={handleReset}>
              <RotateCcw className="h-3 w-3" /> Reiniciar
            </Button>
          </div>
        </div>
      )}

      {/* CA-038a: a send that never reached the agent — kept + retryable */}
      {failedText && (
        <div className="mx-auto flex w-full max-w-3xl items-start gap-2 px-4 pb-2 text-xs">
          <div className="flex flex-1 items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-medium">No se envió tu mensaje</p>
              <p className="mt-0.5 truncate opacity-80">"{failedText}"</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-6 gap-1 px-2 text-[11px]"
                disabled={sending}
                onClick={() => doSend(failedText)}
              >
                <RotateCcw className="h-3 w-3" /> Reintentar
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setFailedText(null)} title="Descartar">
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <ChatInput
        loading={stream.loading || sending}
        onSend={(t) => { void doSend(t); }}
        onCancel={handleReset}
      />
    </div>
  );
}
